import { useRouter } from "next/navigation";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";

import { useStateContext } from "./context-provider";

const SocketContext = createContext();

const sanitizeItemsForServer = (items) => {
    const sanitized = {};
    if (!items) {
        return sanitized;
    }

    for (const [id, item] of Object.entries(items)) {
        if (item) {
            const itemId = item.uuid || item.id || id;
            sanitized[itemId] = {
                uuid: itemId,
                status: item.status,
                ...(item.position ? { position: item.position } : {}),
                ...(item.rotation ? { rotation: item.rotation } : {}),
                ...(item.state ? { state: item.state } : {}),
            };
        }
    }
    return sanitized;
};

const mergeServerItemsWithLocal = (prevItems, serverItems) => {
    if (!serverItems) {
        return prevItems;
    }
    const updated = { ...prevItems };

    for (const [id, serverItem] of Object.entries(serverItems)) {
        const itemId = serverItem.uuid || serverItem.id || id;
        const existing = updated[itemId] || {};
        const mergedState = serverItem.state ? { ...existing.state, ...serverItem.state } : existing.state;

        updated[itemId] = {
            ...existing,
            ...serverItem,
            uuid: itemId,
            ...(mergedState ? { state: mergedState } : {}),
        };
    }

    return updated;
};

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [userId, setUserId] = useState(null);
    const [lobbyTimer, setLobbyTimer] = useState(null);
    const [lobbyId, setLobbyId] = useState(null);
    const [lobbyUsers, setLobbyUsers] = useState([]);
    const [trigger, setTrigger] = useState(null);
    const [joined, setJoined] = useState(false);

    const { setItems, setSolved, pickedup, environment, setEnvironment, focusedItem, setFocusedItem, roomUrl, endScreenOpen } =
        useStateContext();
    const router = useRouter();

    const focusedItemRef = useRef(focusedItem);
    useEffect(() => {
        focusedItemRef.current = focusedItem;
    }, [focusedItem]);

    // 1. Socket Instanziierung
    useEffect(() => {
        //TODO: const socketInstance = io("http://localhost:8866");
        const socketInstance = io(globalThis.location.origin);

        socketInstance.on("connect", () => {
            setUserId(socketInstance.id);
        });

        socketInstance.on("disconnect", () => {
            setUserId(null);
        });

        socketInstance.on("connect_error", (err) => {
            console.error("Websocket connection error:", err);
        });

        setSocket(socketInstance);

        return () => {
            socketInstance.disconnect();
        };
    }, []);

    const syncItemToStorage = (itemId, status, position, rotation, customState, roomUrl, lobbyId) => {
        if (!roomUrl || lobbyId) {
            return;
        }

        try {
            const data = JSON.parse(localStorage.getItem(roomUrl) || "{}");
            if (!data.items) {
                data.items = {};
            }

            const existingItem = data.items[itemId] || {};
            const mergedState = customState ? { ...existingItem.state, ...customState } : existingItem.state;

            data.items[itemId] = {
                ...existingItem,
                status,
                ...(position ? { position } : {}),
                ...(rotation ? { rotation } : {}),
                ...(mergedState ? { state: mergedState } : {}),
            };

            localStorage.setItem(roomUrl, JSON.stringify(data));
        } catch (error) {
            console.error("Fehler beim Speichern des Items im LocalStorage:", error);
        }
    };

    const syncEnvironmentToStorage = (key, value, roomUrl, lobbyId) => {
        if (!roomUrl || lobbyId) {
            return;
        }

        try {
            const data = JSON.parse(localStorage.getItem(roomUrl) || "{}");
            data.environment = {
                ...data.environment,
                [key]: value,
            };

            localStorage.setItem(roomUrl, JSON.stringify(data));
        } catch (error) {
            console.error("Fehler beim Speichern des Environments im LocalStorage:", error);
        }
    };

    const updateItem = useCallback(
        (itemOrId, status, position = null, rotation = null, customState = null) => {
            const itemId = typeof itemOrId === "string" ? itemOrId : itemOrId?.uuid;
            if (!itemId) {
                return;
            }

            const itemObj = typeof itemOrId === "object" ? itemOrId : null;

            setItems((prev) => {
                const current = prev[itemId] || itemObj || {};
                const mergedState = customState ? { ...current.state, ...customState } : current.state;

                return {
                    ...prev,
                    [itemId]: {
                        ...current,
                        ...itemObj,
                        uuid: itemId,
                        status,
                        ...(position ? { position } : {}),
                        ...(rotation ? { rotation } : {}),
                        ...(mergedState ? { state: mergedState } : {}),
                    },
                };
            });

            syncItemToStorage(itemId, status, position, rotation, customState, roomUrl, lobbyId);

            if (socket && lobbyId) {
                socket.emit("updateItem", {
                    lobbyId,
                    itemId,
                    status,
                    position,
                    rotation,
                    state: customState,
                });
            }
        },
        [socket, lobbyId, roomUrl, setItems, setFocusedItem, pickedup]
    );

    const registerItem = useCallback(
        (defaultItem, defaultStatus = "world") => {
            const itemId = defaultItem?.uuid;
            if (!itemId) {
                return;
            }

            setItems((prev) => {
                const existing = prev[itemId];

                if (existing && existing.mesh) {
                    return prev;
                }

                const storedData = roomUrl && !lobbyId ? JSON.parse(localStorage.getItem(roomUrl) || "{}") : {};
                const storedItem = storedData.items?.[itemId] || (storedData.pickedup?.includes(itemId) ? { status: "pickedup" } : null);
                const mergedState = {
                    ...defaultItem?.state,
                    ...storedItem?.state,
                    ...existing?.state,
                };

                return {
                    ...prev,
                    [itemId]: {
                        ...defaultItem,
                        status: existing?.status || storedItem?.status || defaultItem.status || defaultStatus,
                        position: existing?.position || storedItem?.position || defaultItem.position,
                        rotation: existing?.rotation || storedItem?.rotation || defaultItem.rotation,
                        ...(Object.keys(mergedState).length > 0 ? { state: mergedState } : {}),
                    },
                };
            });
        },
        [roomUrl, lobbyId, setItems]
    );

    const syncPuzzleToStorageAndDB = async (puzzleId, isSolved, subTasks, roomUrl, lobbyId) => {
        if (!roomUrl) {
            return;
        }

        let wasAlreadySolved = false;

        if (!lobbyId) {
            try {
                const oldData = JSON.parse(localStorage.getItem(roomUrl) || "{}");
                const previousSolved = oldData.solved || {};
                const prevPuzzle = previousSolved[puzzleId] || {};

                wasAlreadySolved = prevPuzzle.isSolved === true;

                const updatedPuzzle = {
                    ...prevPuzzle,
                    isSolved: isSolved ?? prevPuzzle.isSolved ?? false,
                    subTasks: {
                        ...prevPuzzle.subTasks,
                        ...subTasks,
                    },
                };

                oldData.solved = {
                    ...previousSolved,
                    [puzzleId]: updatedPuzzle,
                };

                localStorage.setItem(roomUrl, JSON.stringify(oldData));
            } catch (error) {
                console.error("Fehler beim Speichern im LocalStorage:", error);
            }
        }
        if (isSolved && !wasAlreadySolved) {
            const oldData = JSON.parse(localStorage.getItem(roomUrl) || "{}");
            const sessionId = oldData.sessionId;

            if (sessionId) {
                try {
                    await fetch("/api/ratings", {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            sessionId,
                            solvedItem: {
                                name: puzzleId,
                                solvedAt: new Date(),
                            },
                        }),
                    });
                } catch (error) {
                    console.error("Fehler beim Sichern des gelösten Rätsels in der DB:", error);
                }
            }
        }
    };

    const updateSolved = useCallback(
        async (puzzleId, isSolved, subTasks) => {
            setSolved((prev) => ({
                ...prev,
                [puzzleId]: {
                    isSolved: isSolved ?? prev?.[puzzleId]?.isSolved ?? false,
                    subTasks: { ...prev?.[puzzleId]?.subTasks, ...subTasks },
                },
            }));

            syncPuzzleToStorageAndDB(puzzleId, isSolved, subTasks, roomUrl, lobbyId);

            if (socket && lobbyId) {
                socket.emit("updateSolved", { lobbyId, puzzleId, isSolved, subTasks });
            }
        },
        [socket, lobbyId, roomUrl, setSolved]
    );

    useEffect(() => {
        if (endScreenOpen && socket && lobbyId) {
            socket.emit("finishRoom", { lobbyId });
        }
    }, [endScreenOpen, socket, lobbyId]);

    const updateEnvironment = useCallback(
        (key, valueOrUpdater) => {
            setEnvironment((prev) => {
                const currentVal = prev?.[key];
                const newVal = typeof valueOrUpdater === "function" ? valueOrUpdater(currentVal) : valueOrUpdater;

                syncEnvironmentToStorage(key, newVal, roomUrl, lobbyId);

                if (socket && lobbyId) {
                    socket.emit("updateEnvironment", { lobbyId, key, value: newVal });
                }

                return {
                    ...prev,
                    [key]: newVal,
                };
            });
        },
        [socket, lobbyId, roomUrl, setEnvironment]
    );

    const updateTrigger = useCallback(
        (triggerName) => {
            setTrigger(triggerName);
            if (socket && lobbyId) {
                socket.emit("updateTrigger", { lobbyId, trigger: triggerName });
            }
        },
        [socket, lobbyId]
    );

    // 5. Lobby Actions
    const createLobby = useCallback(
        (userData, initialData = {}, callback) => {
            if (!socket) {
                return;
            }

            let existingStartTime = null;
            if (roomUrl) {
                try {
                    const stored = JSON.parse(localStorage.getItem(roomUrl) || "{}");
                    const acc = stored.elapsedTime || 0;
                    const start = stored.startTime;

                    if (start) {
                        const activeDiff = Math.floor((Date.now() - start) / 1000);
                        existingStartTime = Date.now() - (acc + activeDiff) * 1000;
                    } else if (acc > 0) {
                        existingStartTime = Date.now() - acc * 1000;
                    }
                } catch (error) {
                    console.error("Fehler beim Lesen der Singleplayer-Zeit:", error);
                }
            }

            const payload = {
                user: userData,
                items: sanitizeItemsForServer(initialData.items),
                puzzles: initialData.puzzles || {},
                environment: initialData.environment || environment || {},
                startTime: existingStartTime,
            };

            socket.emit("createLobby", payload, (lobby) => {
                if (lobby && !("error" in lobby)) {
                    setLobbyId(lobby.lobbyId);
                    setLobbyUsers(lobby.users);

                    if (lobby.startTime) {
                        setLobbyTimer({ startTime: lobby.startTime, endTime: lobby.endTime });
                    }

                    if (lobby.items) {
                        setItems((prev) => mergeServerItemsWithLocal(prev, lobby.items));
                    }
                    if (lobby.puzzles) {
                        setSolved((prev) => ({ ...prev, ...lobby.puzzles }));
                    }
                    if (lobby.environment) {
                        setEnvironment((prev) => ({ ...prev, ...lobby.environment }));
                    }
                }
                callback?.(lobby);
            });
        },
        [socket, roomUrl, setItems, setSolved, setEnvironment, environment]
    );

    const joinLobby = useCallback(
        (idToJoin, userData, callback) => {
            if (!socket) {
                return;
            }
            socket.emit("joinLobby", idToJoin, userData, (result) => {
                if ("error" in result) {
                    callback?.(result);
                    return;
                }
                setLobbyTimer({ startTime: result.startTime, endTime: result.endTime });
                setLobbyId(result.lobbyId);
                setLobbyUsers(result.users);
                if (result.items) {
                    setItems((prev) => mergeServerItemsWithLocal(prev, result.items));
                }
                if (result.puzzles) {
                    setSolved((prev) => ({ ...prev, ...result.puzzles }));
                }
                if (result.environment) {
                    setEnvironment((prev) => ({ ...prev, ...result.environment }));
                }
                callback?.(result);
            });
        },
        [socket, setItems, setSolved, setEnvironment]
    );

    const startLobby = useCallback(
        (room) => {
            if (!socket || !lobbyId) {
                return;
            }
            socket.emit("startLobby", { lobbyId, room });
        },
        [socket, lobbyId]
    );

    // 6. Player Movement & Appearance Actions
    const updatePlayerPosition = useCallback(
        (x, y, z, rotation) => {
            if (!socket || !lobbyId) {
                return;
            }
            socket.emit("updatePosition", { lobbyId, userId, x, y, z, rotation });
        },
        [socket, lobbyId, userId]
    );

    const updatePlayerAppearance = useCallback(
        ({ name, color }) => {
            if (!socket || !lobbyId) {
                return;
            }
            socket.emit("updateAppearance", { lobbyId, userId, name, color });
        },
        [socket, lobbyId, userId]
    );

    // 7. Incoming Socket Event Listeners
    useEffect(() => {
        if (!socket) {
            return;
        }

        const handlePlayerJoined = (newPlayer) => {
            setLobbyUsers((prev) => [...prev, newPlayer]);
        };

        const handlePlayerLeft = (playerId) => {
            setLobbyUsers((prev) => prev.filter((user) => user.id !== playerId));
        };

        const handlePlayerMoved = ({ id, x, y, z, rotation }) => {
            setLobbyUsers((prev) => prev.map((user) => (user.id === id ? { ...user, x, y, z, rotation } : user)));
        };

        const handlePlayerAppearance = ({ id, name, color }) => {
            setLobbyUsers((prev) => prev.map((user) => (user.id === id ? { ...user, name, color } : user)));
        };

        const handleStartLobby = ({ room }) => {
            router.push(`/escape-room/${room}?lobby=${lobbyId}`);
        };

        const handleItemUpdated = ({ itemId, status, position, rotation, state }) => {
            setItems((prev) => {
                const existing = prev[itemId] || { uuid: itemId };
                const mergedState = state ? { ...existing.state, ...state } : existing.state;

                return {
                    ...prev,
                    [itemId]: {
                        ...existing,
                        uuid: itemId,
                        status,
                        ...(position ? { position } : {}),
                        ...(rotation ? { rotation } : {}),
                        ...(mergedState ? { state: mergedState } : {}),
                    },
                };
            });
        };

        const handlePuzzleUpdated = ({ puzzleId, isSolved, subTasks }) => {
            setSolved((prev) => ({
                ...prev,
                [puzzleId]: {
                    isSolved: isSolved ?? prev?.[puzzleId]?.isSolved ?? false,
                    subTasks: { ...prev?.[puzzleId]?.subTasks, ...subTasks },
                },
            }));
        };

        const handleEnvironmentUpdated = ({ key, value }) => {
            setEnvironment((prev) => ({
                ...prev,
                [key]: value,
            }));
        };

        const handleTriggerUpdated = (incomingTrigger) => {
            setTrigger(incomingTrigger);
        };

        const handleRoomFinished = ({ endTime }) => {
            setLobbyTimer((prev) => ({ ...prev, endTime }));
        };

        const handleRoomStarted = ({ startTime }) => {
            setLobbyTimer((prev) => ({
                ...prev,
                startTime,
            }));
        };

        socket.on("roomStarted", handleRoomStarted);
        socket.on("playerJoined", handlePlayerJoined);
        socket.on("playerLeft", handlePlayerLeft);
        socket.on("playerMoved", handlePlayerMoved);
        socket.on("playerChangedAppearance", handlePlayerAppearance);
        socket.on("startLobby", handleStartLobby);
        socket.on("itemUpdated", handleItemUpdated);
        socket.on("puzzleUpdated", handlePuzzleUpdated);
        socket.on("environmentUpdated", handleEnvironmentUpdated);
        socket.on("triggerUpdated", handleTriggerUpdated);
        socket.on("roomFinished", handleRoomFinished);

        return () => {
            socket.off("playerJoined", handlePlayerJoined);
            socket.off("playerLeft", handlePlayerLeft);
            socket.off("playerMoved", handlePlayerMoved);
            socket.off("playerChangedAppearance", handlePlayerAppearance);
            socket.off("startLobby", handleStartLobby);
            socket.off("itemUpdated", handleItemUpdated);
            socket.off("puzzleUpdated", handlePuzzleUpdated);
            socket.off("environmentUpdated", handleEnvironmentUpdated);
            socket.off("triggerUpdated", handleTriggerUpdated);
            socket.off("roomFinished", handleRoomFinished);
            socket.off("roomStarted", handleRoomStarted);
        };
    }, [socket, lobbyId, setItems, setSolved, setEnvironment, setFocusedItem, router, pickedup]);

    // 8. Memoized Context Value
    const contextValue = useMemo(
        () => ({
            socket,
            setSocket,
            lobbyId,
            lobbyTimer,
            setLobbyId,
            userId,
            lobbyUsers,
            setLobbyUsers,
            createLobby,
            joinLobby,
            startLobby,
            updatePlayerPosition,
            updatePlayerAppearance,
            updateItem,
            registerItem,
            updateSolved,
            updateEnvironment,
            updateTrigger,
            trigger,
            setTrigger,
            joined,
            setJoined,
        }),
        [
            socket,
            lobbyId,
            lobbyTimer,
            userId,
            lobbyUsers,
            trigger,
            joined,
            createLobby,
            joinLobby,
            startLobby,
            updatePlayerPosition,
            updatePlayerAppearance,
            updateItem,
            registerItem,
            updateSolved,
            updateEnvironment,
            updateTrigger,
        ]
    );

    return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>;
};

export const useSocketContext = () => useContext(SocketContext);
